import { useEffect, useState } from 'react';
import { can, faNum, formatPercent, formatRial } from '@kia/shared';
import { api, errMsg } from '../api/client';
import { useApiResource } from '../lib/useApiResource';
import { useAuth } from '../auth/AuthProvider';
import { Button, Card, CostConfidence, Field, Input, Modal, NumberInput, useToast } from '../ui';

interface MasterProduct {
  code: string;
  name: string;
  family: string;
  fullCost: number;
  isNew: boolean;
  active: boolean;
  productType: string;
  // Cost view + completeness (E16-02): breakdown, suggested price, and per-line price-source counts.
  material: number;
  freight: number;
  conversion: number;
  suggestedAtFloor: number;
  /** price that hits the AIMED-for target margin (E16 margin-thread) — what the master list steers to. */
  suggestedAtTarget: number;
  target: number;
  confirmed: number;
  actualLines: number;
  groupLines: number;
  missingLines: number;
}
interface MasterMaterial {
  code: string;
  name: string;
  group: string;
  unit: string;
  price: number | null;
  source: 'actual' | 'group' | null;
}
interface BomDetailLine {
  material_code: number | null;
  name: string;
  qty: number;
  unit: string;
  unit_price: number | null;
  line_cost: number;
  source?: 'actual' | 'group' | null;
}
interface BomDetail {
  material: number;
  full: number;
  lines: BomDetailLine[];
  /** fat-finger guard: present when the product's material cost is wildly off its family median. */
  costOutlier?: { material: number; familyMedian: number; ratio: number };
}

/**
 * «محصولات و مواد» — the master-data screen (R3 / R2). Lists the full product master (reference ⊕
 * operator-added) with COGS, and lets an admin add a new product/material and edit a NEW product's BOM. The
 * 307 reference products are read-only (the golden engine depends on them); only «نو» products are editable.
 */
export function MasterDataScreen() {
  const { user } = useAuth();
  const canEdit = user ? can(user.role, 'config.write') : false;
  const toast = useToast();
  const [q, setQ] = useState('');
  const { data, status, reload } = useApiResource<MasterProduct[]>(
    `/api/master/products?q=${encodeURIComponent(q)}`,
    [q],
  );
  const list = data ?? [];

  const [newProduct, setNewProduct] = useState(false);
  const [bomFor, setBomFor] = useState<{ code: string; name: string } | null>(null);

  async function toggleActive(p: MasterProduct) {
    const r = await api(`/api/master/products/${p.code}/active`, {
      method: 'POST',
      body: JSON.stringify({ active: !p.active }),
    });
    if (!r.ok) return toast(errMsg(r), 'error');
    toast(p.active ? 'غیرفعال شد' : 'فعال شد');
    reload(true);
  }

  return (
    <>
      <Card title="محصولات و مواد — فهرست کامل (مرجع + افزوده‌شده)">
        <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Field label="جستجو (کد یا نام)">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="کد یا نام محصول…"
            />
          </Field>
          {canEdit ? (
            <Button onClick={() => setNewProduct(true)}>+ محصول جدید</Button>
          ) : (
            <span className="muted" style={{ fontSize: 12 }}>
              افزودن/ویرایش فقط برای مدیر
            </span>
          )}
        </div>

        {status === 'loading' ? (
          <p className="muted">در حال بارگذاری…</p>
        ) : (
          <div style={{ maxHeight: 540, overflowY: 'auto', marginTop: 12 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>کد</th>
                  <th>نام</th>
                  <th>خانواده</th>
                  <th>نوع</th>
                  <th>بهای تمام‌شده (﷼)</th>
                  <th>اطمینان بها</th>
                  <th>پیشنهاد قیمت (﷼)</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {list.map((p) => (
                  <tr key={p.code} style={{ opacity: p.active ? 1 : 0.5 }}>
                    <td className="num">
                      {p.code}
                      {p.isNew ? <span className="muted"> · نو</span> : null}
                    </td>
                    <td>{p.name || '—'}</td>
                    <td>{p.family || '—'}</td>
                    <td>{p.productType || '—'}</td>
                    <td
                      className="num"
                      title={`مواد ${formatRial(p.material)} + حمل ${formatRial(p.freight)} + دستمزد و سربار ${formatRial(p.conversion)}`}
                    >
                      {formatRial(p.fullCost)}
                    </td>
                    <td>
                      <CostConfidence
                        confirmed={p.confirmed}
                        actualLines={p.actualLines}
                        groupLines={p.groupLines}
                        missingLines={p.missingLines}
                      />
                    </td>
                    <td
                      className="num"
                      title={`بها ÷ (۱ − هدف سود) — قیمت پیشنهادی برای رسیدن به هدف حاشیهٔ ${formatPercent(p.target)}`}
                    >
                      {p.suggestedAtTarget > 0 ? formatRial(p.suggestedAtTarget) : '—'}
                    </td>
                    <td>
                      {p.isNew && canEdit ? (
                        <div className="row" style={{ gap: 6 }}>
                          <Button variant="ghost" onClick={() => setBomFor(p)}>
                            ویرایش BOM
                          </Button>
                          <Button variant="ghost" onClick={() => void toggleActive(p)}>
                            {p.active ? 'غیرفعال' : 'فعال'}
                          </Button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="muted" style={{ marginTop: 8 }}>
          {list.length} محصول. «نو» = افزوده‌شده توسط اپراتور (قابل ویرایش)؛ بقیه مرجع و
          تغییرناپذیرند.
        </p>
      </Card>

      {newProduct ? (
        <NewProductModal
          onClose={() => setNewProduct(false)}
          onCreated={(code, name) => {
            setNewProduct(false);
            reload(true);
            setBomFor({ code, name }); // straight into the BOM editor for the new product
          }}
        />
      ) : null}

      {bomFor ? (
        <BomEditor
          code={bomFor.code}
          name={bomFor.name}
          onClose={() => {
            setBomFor(null);
            reload(true);
          }}
        />
      ) : null}
    </>
  );
}

function NewProductModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (code: string, name: string) => void;
}) {
  const toast = useToast();
  const [family, setFamily] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!name.trim()) return toast('نام محصول لازم است', 'error');
    setBusy(true);
    const r = await api<{ code: string }>('/api/master/products', {
      method: 'POST',
      body: JSON.stringify({ family: family.trim(), name: name.trim() }),
    });
    setBusy(false);
    if (!r.ok) return toast(errMsg(r), 'error');
    toast('محصول ساخته شد — اکنون BOM را وارد کنید');
    onCreated((r.body as { code: string }).code, name.trim());
  }

  return (
    <Modal
      title="محصول جدید"
      onClose={onClose}
      actions={
        <>
          <Button onClick={() => void save()} loading={busy} disabled={!name.trim()}>
            ساختن و ورود BOM
          </Button>
          <Button variant="secondary" onClick={onClose}>
            انصراف
          </Button>
        </>
      }
    >
      <Field label="نام محصول" required>
        <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </Field>
      <Field label="خانواده" optional>
        <Input value={family} onChange={(e) => setFamily(e.target.value)} />
      </Field>
      <p className="muted" style={{ fontSize: 12 }}>
        کد به‌صورت خودکار در محدودهٔ کدهای جدید تخصیص می‌یابد و هرگز با کدهای مرجع تداخل ندارد.
      </p>
    </Modal>
  );
}

interface EditLine {
  materialCode: string;
  name: string;
  unit: string;
  qty: string;
  /** effective unit price (null ⇒ unpriced) + its source — drives the LIVE material cost as lines change. */
  price: number | null;
  source: 'actual' | 'group' | null;
}

function BomEditor({ code, name, onClose }: { code: string; name: string; onClose: () => void }) {
  const toast = useToast();
  const [lines, setLines] = useState<EditLine[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cost, setCost] = useState<number | null>(null);
  const [newMaterial, setNewMaterial] = useState(false);

  useEffect(() => {
    void (async () => {
      const r = await api<BomDetail>(`/api/master/products/${code}/bom`);
      if (r.ok) {
        const b = r.body as BomDetail;
        setLines(
          b.lines.map((l) => ({
            materialCode: String(l.material_code ?? ''),
            name: l.name,
            unit: l.unit,
            qty: String(l.qty),
            price: l.unit_price,
            source: l.source ?? (l.unit_price != null ? 'group' : null),
          })),
        );
        setCost(b.material);
      }
      setLoaded(true);
    })();
  }, [code]);

  function addLine(m: MasterMaterial) {
    if (lines.some((l) => l.materialCode === m.code))
      return toast('این ماده قبلاً اضافه شده', 'error');
    setLines((ls) => [
      ...ls,
      {
        materialCode: m.code,
        name: m.name,
        unit: m.unit,
        qty: '1',
        price: m.price,
        source: m.source,
      },
    ]);
  }

  async function save() {
    const payload = lines.map((l) => ({ materialCode: l.materialCode, qty: Number(l.qty) }));
    if (payload.some((l) => !Number.isFinite(l.qty) || l.qty <= 0))
      return toast('مقدار هر ردیف باید بزرگ‌تر از صفر باشد', 'error');
    setBusy(true);
    const r = await api<BomDetail>(`/api/master/products/${code}/bom`, {
      method: 'PUT',
      body: JSON.stringify({ lines: payload }),
    });
    setBusy(false);
    if (!r.ok) return toast(errMsg(r), 'error');
    toast('BOM ذخیره شد');
    // Fat-finger guard: the save succeeded, but warn if the material cost is far off the family median.
    const outlier = (r.body as BomDetail).costOutlier;
    if (outlier) {
      toast(
        `⚠ بهای موادِ این محصول ~${outlier.ratio}× میانهٔ خانواده است — احتمال خطای مقدار یا واحد؛ بررسی کنید`,
        'error',
      );
    }
    onClose();
  }

  // Lead the user to a valid BOM (R-A): the mandatory rules the server enforces, surfaced live so the save
  // is only enabled once they're met — and the exact missing thing is named, not discovered on submit.
  const blockers: string[] = [];
  if (lines.length === 0) blockers.push('حداقل یک ماده');
  else if (!lines.every((l) => Number(l.qty) > 0))
    blockers.push('مقدار همهٔ ردیف‌ها باید بزرگ‌تر از صفر باشد');
  const canSave = loaded && blockers.length === 0;

  // Live material cost as the owner builds the BOM (E16-12): Σ qty × effective price. Matches the engine's
  // material sum, so the cost shows immediately — not only after save. Unpriced lines count 0 but are flagged.
  const liveCost = lines.reduce((s, l) => s + Math.max(0, Number(l.qty) || 0) * (l.price ?? 0), 0);
  const unpricedCount = lines.filter((l) => l.price == null && Number(l.qty) > 0).length;

  return (
    <Modal
      title={`BOM — ${name || code}`}
      onClose={onClose}
      actions={
        <>
          <Button onClick={() => void save()} loading={busy} disabled={!canSave}>
            ذخیرهٔ BOM
          </Button>
          <Button variant="secondary" onClick={onClose}>
            بستن
          </Button>
        </>
      }
    >
      {!loaded ? (
        <p className="muted">در حال بارگذاری…</p>
      ) : (
        <>
          <div
            className={blockers.length ? 'banner banner-err' : 'banner banner-ok'}
            style={{ marginBottom: 10 }}
          >
            {blockers.length
              ? `برای ذخیره لازم است: ${blockers.join(' · ')}`
              : `آمادهٔ ذخیره — ${lines.length} ماده`}
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>ماده</th>
                <th>واحد</th>
                <th>
                  مقدار{' '}
                  <span className="field-req" title="الزامی">
                    *
                  </span>
                </th>
                <th>بها</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => {
                const lineCost = l.price != null ? Math.max(0, Number(l.qty) || 0) * l.price : null;
                return (
                  <tr key={l.materialCode}>
                    <td>
                      {l.name} <span className="muted num">({l.materialCode})</span>
                    </td>
                    <td>{l.unit}</td>
                    <td style={{ maxWidth: 120 }}>
                      <NumberInput
                        allowDecimal
                        value={l.qty}
                        onValue={(n) =>
                          setLines((ls) =>
                            ls.map((x, j) => (j === i ? { ...x, qty: String(n) } : x)),
                          )
                        }
                      />
                    </td>
                    <td className="num">
                      {lineCost != null ? (
                        formatRial(lineCost)
                      ) : (
                        <span className="warn" title="این ماده قیمت ندارد">
                          بدون قیمت ⚠
                        </span>
                      )}
                    </td>
                    <td>
                      <Button
                        variant="ghost"
                        onClick={() => setLines((ls) => ls.filter((_x, j) => j !== i))}
                      >
                        حذف
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {lines.length === 0 ? (
                <tr>
                  <td colSpan={5} className="muted">
                    هنوز ردیفی اضافه نشده — یک ماده جستجو و اضافه کنید.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>

          <MaterialPicker onPick={addLine} onNewMaterial={() => setNewMaterial(true)} />

          <p className="muted" style={{ marginTop: 10 }}>
            بهای مواد (زنده): <b className="num">{formatRial(liveCost)}</b>
            {unpricedCount > 0 ? (
              <span className="warn">
                {' '}
                — {faNum(unpricedCount)} قلم بدون قیمت؛ بها کمتر از واقعیت است
              </span>
            ) : null}
            {cost != null && Math.round(cost) !== Math.round(liveCost) ? (
              <span> · ثبت‌شده: {formatRial(cost)}</span>
            ) : null}
            . حمل و دستمزد/سربار پس از ذخیره به بهای تمام‌شده افزوده می‌شوند.
          </p>
        </>
      )}

      {newMaterial ? (
        <NewMaterialModal
          onClose={() => setNewMaterial(false)}
          onCreated={(m) => {
            setNewMaterial(false);
            addLine(m);
          }}
        />
      ) : null}
    </Modal>
  );
}

function MaterialPicker({
  onPick,
  onNewMaterial,
}: {
  onPick: (m: MasterMaterial) => void;
  onNewMaterial: () => void;
}) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<MasterMaterial[]>([]);

  useEffect(() => {
    if (!q.trim()) return setHits([]);
    let live = true;
    const t = setTimeout(async () => {
      const r = await api<MasterMaterial[]>(`/api/master/materials?q=${encodeURIComponent(q)}`);
      if (live && r.ok) setHits(r.body as MasterMaterial[]);
    }, 200);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [q]);

  return (
    <div style={{ marginTop: 8 }}>
      <div className="row" style={{ gap: 8, alignItems: 'flex-end' }}>
        <Field label="افزودن ماده (جستجوی کد یا نام)">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="مثلاً سیم…" />
        </Field>
        <Button variant="secondary" onClick={onNewMaterial}>
          + مادهٔ جدید
        </Button>
      </div>
      {hits.length ? (
        <div
          style={{
            border: '1px solid var(--line)',
            borderRadius: 8,
            marginTop: 6,
            maxHeight: 180,
            overflowY: 'auto',
          }}
        >
          {hits.map((m) => (
            <button
              key={m.code}
              type="button"
              className="search-item"
              style={{ display: 'block', width: '100%', textAlign: 'start', padding: '6px 10px' }}
              onClick={() => {
                onPick(m);
                setQ('');
                setHits([]);
              }}
            >
              <b>{m.name}</b> <span className="muted num">({m.code})</span>{' '}
              <span className="muted">
                — {m.group} · {m.unit}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function NewMaterialModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (m: MasterMaterial) => void;
}) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [grp, setGrp] = useState('');
  const [unit, setUnit] = useState('');
  const [busy, setBusy] = useState(false);
  const { data } =
    useApiResource<{ key: string; group: string; unit: string }[]>('/api/groupunits');
  const groups = [...new Set((data ?? []).map((g) => g.group))];
  const units = [...new Set((data ?? []).map((g) => g.unit))];

  async function save() {
    if (!name.trim() || !grp.trim() || !unit.trim())
      return toast('نام، گروه و واحد لازم‌اند', 'error');
    setBusy(true);
    const r = await api<{ code: string }>('/api/master/materials', {
      method: 'POST',
      body: JSON.stringify({ name: name.trim(), grp: grp.trim(), unit: unit.trim() }),
    });
    setBusy(false);
    if (!r.ok) return toast(errMsg(r), 'error');
    toast('ماده ساخته شد');
    // A brand-new material has no price yet — it'll flag «بدون قیمت» in the BOM until priced.
    onCreated({
      code: (r.body as { code: string }).code,
      name: name.trim(),
      group: grp,
      unit,
      price: null,
      source: null,
    });
  }

  return (
    <Modal
      title="مادهٔ جدید"
      onClose={onClose}
      actions={
        <>
          <Button
            onClick={() => void save()}
            loading={busy}
            disabled={!name.trim() || !grp.trim() || !unit.trim()}
          >
            ساختن و افزودن
          </Button>
          <Button variant="secondary" onClick={onClose}>
            انصراف
          </Button>
        </>
      }
    >
      <Field label="نام ماده" required>
        <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </Field>
      <Field
        label="گروه (برای قیمت‌گذاری گروهی)"
        required
        hint="گروه، قیمت‌گذاری و بهای این ماده را تعیین می‌کند"
      >
        <Input value={grp} onChange={(e) => setGrp(e.target.value)} list="mat-groups" />
        <datalist id="mat-groups">
          {groups.map((g) => (
            <option key={g} value={g} />
          ))}
        </datalist>
      </Field>
      <Field label="واحد" required>
        <Input value={unit} onChange={(e) => setUnit(e.target.value)} list="mat-units" />
        <datalist id="mat-units">
          {units.map((u) => (
            <option key={u} value={u} />
          ))}
        </datalist>
      </Field>
      <p className="muted" style={{ fontSize: 12 }}>
        بهتر است گروه از میان گروه‌های موجود انتخاب شود تا قیمت گروهی روی این ماده اعمال شود.
      </p>
    </Modal>
  );
}
